import { useEffect, useMemo, useState } from "react";
import { api } from "@/api/client";
import { useStore } from "@/state/store";
import { IconCaretDown, IconCaretUp } from "@/components/SvoIcons";
import type { AuditAction, AuditEntry } from "@offsite/shared";

const ACTION_LABEL: Record<AuditAction, string> = {
  "topic.add": "Thema hinzugefügt",
  "topic.update": "Thema umbenannt",
  "topic.delete": "Thema gelöscht",
  "topic.publish-list": "Themenliste veröffentlicht",
  "req.put": "Anforderung gespeichert",
  "req.submit": "Anforderung eingereicht",
  "vote.cast": "Stimme abgegeben",
  "vote.reset": "Stimmen zurückgesetzt",
  "phase.set": "Phase gewechselt",
  "framing.set": "Rahmenthema gesetzt",
  "fallback.set": "Notfall-Modus umgeschaltet",
  "winner.set": "Sieger gesetzt",
  "teams.assign": "Teams verteilt",
  "team.join": "Eigenem Team beigetreten",
  "team.name": "Teamname gesetzt",
  "team.req.put": "Team-Anforderung gespeichert",
  "team.req.submit": "Team-Anforderung eingereicht",
  "team.req.reopen": "Team-Anforderung wieder geöffnet",
  "evaluate.trigger": "KI-Bewertung gestartet",
  "overnight.trigger": "Overnight-Job gestartet",
  "admin.reset": "Reset / Archiviert",
  "magic-link.use": "Reviewer-Magic-Link verwendet",
  "code.create": "Einmal-Codes erzeugt",
  "team.identity": "Teilnehmer-Identität gesetzt",
};

const ACTION_KLASS: Record<AuditAction, string> = {
  "topic.add": "act-add",
  "topic.update": "act-update",
  "topic.delete": "act-delete",
  "topic.publish-list": "act-publish",
  "req.put": "act-req",
  "req.submit": "act-submit",
  "vote.cast": "act-vote",
  "vote.reset": "act-reset",
  "phase.set": "act-phase",
  "framing.set": "act-phase",
  "fallback.set": "act-phase",
  "winner.set": "act-phase",
  "teams.assign": "act-teams",
  "team.join": "act-teams",
  "team.name": "act-teams",
  "team.req.put": "act-req",
  "team.req.submit": "act-submit",
  "team.req.reopen": "act-req",
  "evaluate.trigger": "act-ki",
  "overnight.trigger": "act-ki",
  "admin.reset": "act-reset",
  "magic-link.use": "act-reset",
  "code.create": "act-teams",
  "team.identity": "act-teams",
};

type Filter = "all" | "topics" | "req" | "votes" | "phase" | "ki";

const FILTERS: { id: Filter; label: string; matches: (a: AuditAction) => boolean }[] = [
  { id: "all", label: "Alles", matches: () => true },
  { id: "topics", label: "Themen", matches: (a) => a.startsWith("topic.") },
  { id: "req", label: "Anforderungen", matches: (a) => a.startsWith("req.") },
  { id: "votes", label: "Abstimmung", matches: (a) => a.startsWith("vote.") },
  { id: "phase", label: "Phasen", matches: (a) => a === "phase.set" },
  {
    id: "ki",
    label: "KI",
    matches: (a) => a === "evaluate.trigger" || a === "overnight.trigger",
  },
];

export function AdminAuditSection() {
  const { state } = useStore();
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [filter, setFilter] = useState<Filter>("all");
  const [open, setOpen] = useState<Set<string>>(new Set());

  const reload = async () => {
    try {
      const r = await api.admin.audit(state.auth.token);
      setEntries(r.entries);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    reload();
    const id = setInterval(reload, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.auth.token]);

  const filtered = useMemo(() => {
    const f = FILTERS.find((x) => x.id === filter)!;
    return entries.filter((e) => f.matches(e.action));
  }, [entries, filter]);

  const toggle = (id: string) => {
    setOpen((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="admin-grid">
      <header className="admin-section-head">
        <h2>Verlauf</h2>
        <p>
          Jede Mutation mit Zeitstempel — neueste oben. Wer was wann eingegeben/geändert hat.
          14-Tage-TTL.
        </p>
      </header>

      <div className="filter-pillset">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            className={`filter-pill${filter === f.id ? " is-active" : ""}`}
          >
            {f.label}
          </button>
        ))}
        <span className="filter-pill-count">{filtered.length} Einträge</span>
      </div>

      <div style={{ display: "grid", gap: 8 }}>
        {filtered.length === 0 && (
          <p className="form-hint">Noch keine Einträge.</p>
        )}
        {filtered.map((e) => {
          const isOpen = open.has(e.id);
          const hasPayload = e.payload && Object.keys(e.payload).length > 0;
          return (
            <div key={e.id} className="audit-entry">
              <div className="audit-row">
                <span className={`audit-action ${ACTION_KLASS[e.action]}`}>
                  <span className="dot" />
                  {ACTION_LABEL[e.action]}
                </span>
                <span className="audit-summary">{summarize(e)}</span>
                <code className="audit-time">{fmtTime(e.ts)}</code>
                <span
                  className={`actor-pill${
                    e.actor.role === "presenter" ? " presenter" : ""
                  }`}
                >
                  {e.actor.role === "presenter"
                    ? "Presenter"
                    : `Teilnehmer ${e.actor.sub.slice(0, 6)}`}
                </span>
                {hasPayload && (
                  <button
                    type="button"
                    onClick={() => toggle(e.id)}
                    className="btn-icon"
                    aria-label={isOpen ? "Payload schließen" : "Payload öffnen"}
                  >
                    {isOpen ? <IconCaretUp size={14} /> : <IconCaretDown size={14} />}
                  </button>
                )}
              </div>
              {isOpen && hasPayload && (
                <pre className="audit-payload">
                  {JSON.stringify(e.payload, null, 2)}
                </pre>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function summarize(e: AuditEntry): string {
  const p = e.payload as Record<string, unknown> | undefined;
  switch (e.action) {
    case "topic.add":
      return `„${(p?.topic as { title?: string } | undefined)?.title ?? "—"}"`;
    case "topic.update":
      return `„${p?.before ?? "—"}" → „${p?.after ?? "—"}"`;
    case "topic.delete":
      return `„${p?.title ?? "—"}" entfernt`;
    case "topic.publish-list":
      return `${(p?.topics as unknown[] | undefined)?.length ?? 0} Themen veröffentlicht`;
    case "req.put": {
      const fields = p?.fields as Record<string, string> | undefined;
      const filled = fields ? Object.values(fields).filter((v) => v?.trim()).length : 0;
      const source = p?.source ?? "";
      return `Topic ${e.target?.slice(0, 8) ?? ""} · ${filled} Felder befüllt${source ? ` · ${source}` : ""}`;
    }
    case "req.submit":
      return `Topic ${e.target?.slice(0, 8) ?? ""} eingereicht`;
    case "vote.cast":
      return `für Topic ${e.target?.slice(0, 8) ?? ""}`;
    case "vote.reset":
      return "alle Stimmen gelöscht";
    case "phase.set": {
      const phase = p?.phase ?? "";
      const dur = p?.timerDurationSec as number | undefined;
      return `→ ${phase}${dur ? ` · Timer ${Math.round(dur / 60)}min` : ""}`;
    }
    case "evaluate.trigger":
      return "Bewertungs-Lambda async invoked";
    case "overnight.trigger":
      return "Overnight-Lambda async invoked";
    case "admin.reset": {
      const scope = (p?.scope as string) ?? "?";
      const items = (p?.itemCount as number) ?? 0;
      const label = (p?.label as string) ?? "";
      return `${scope} · ${items} Items archiviert${label ? ` · „${label}"` : ""}`;
    }
    default:
      return "";
  }
}

function fmtTime(ts: number): string {
  const d = new Date(ts);
  const today = new Date();
  const isToday = d.toDateString() === today.toDateString();
  const time = d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  if (isToday) return time;
  return `${d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })} ${time}`;
}
